import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SITE from "@/constants/site";
import { ArrowLeft, ArrowRight, Download, Plus, Search, X } from "lucide-react";

// forgiving contains-match: case, apostrophes, and hyphens don't matter
const norm = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]/g, "");

// Save a photo/video the easiest way the device allows: the native share
// sheet where supported (iPhone: "Save Image" goes straight to the camera
// roll), otherwise a direct download.
export async function saveMedia(item) {
  const ext = item.kind === "video" ? "mp4" : "jpg";
  // neutral filename — never leak how photos are classified
  const stem = (item.id.match(/\d{8}_\d{6}/) || [])[0] || "photo";
  const name = `gilmore-wedding-${stem}.${ext}`;
  try {
    if (navigator.canShare) {
      const blob = await (await fetch(item.full)).blob();
      const file = new File([blob], name, { type: blob.type || (item.kind === "video" ? "video/mp4" : "image/jpeg") });
      if (navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file] });
        return;
      }
    }
  } catch (err) {
    if (err && err.name === "AbortError") return; // user closed the sheet
  }
  const sep = item.full.includes("?") ? "&" : "?";
  window.location.href = `${item.full}${sep}download=${encodeURIComponent(name)}`;
}

// small always-visible save control on each grid cell (a true sibling
// button — never nested inside the photo's own button)
export function CellSave({ item }) {
  const [busy, setBusy] = useState(false);
  return (
    <button
      type="button"
      className="gcell-save"
      aria-label="Save this photo"
      onClick={async (e) => {
        e.stopPropagation();
        setBusy(true);
        await saveMedia(item);
        setBusy(false);
      }}
    >
      {busy ? <span className="gcell-save__dot" aria-hidden="true" /> : <Download size={15} strokeWidth={1.6} />}
    </button>
  );
}

// quiet scroll-reveal for grid cells: fade + rise, staggered, once
export function useReveal(deps) {
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      document.querySelectorAll(".ggrid__cell").forEach((el) => el.classList.add("is-in"));
      return undefined;
    }
    const io = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("is-in");
            io.unobserve(e.target);
          }
        }),
      { threshold: 0.12, rootMargin: "60px" }
    );
    document.querySelectorAll(".ggrid__cell").forEach((el) => io.observe(el));
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

export function Lightbox({ items, index, onClose, onStep, origin }) {
  const item = items[index];
  const touchX = useRef(null);
  const rootRef = useRef(null);
  const innerRef = useRef(null);

  // Move focus into the dialog on open; hand it back to the opener on close.
  useEffect(() => {
    const opener = document.activeElement;
    rootRef.current?.querySelector(".glb__btn--close")?.focus();
    return () => {
      if (opener && typeof opener.focus === "function") opener.focus();
    };
  }, []);

  // Shared-element entrance: the tapped cell grows into the viewer (FLIP).
  useEffect(() => {
    const inner = innerRef.current;
    if (!inner || !origin) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const r = inner.getBoundingClientRect();
    if (!r.width || !r.height) return;
    inner.style.animation = "none";
    inner.style.transformOrigin = "top left";
    inner.style.transition = "none";
    inner.style.transform = `translate(${origin.left - r.left}px, ${origin.top - r.top}px) scale(${origin.width / r.width}, ${origin.height / r.height})`;
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        inner.style.transition = "transform 0.38s cubic-bezier(0.22, 0.61, 0.36, 1)";
        inner.style.transform = "";
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") onStep(1);
      if (e.key === "ArrowLeft") onStep(-1);
      if (e.key === "Tab") {
        // trap focus inside the dialog
        const root = rootRef.current;
        if (!root) return;
        const els = [...root.querySelectorAll("button, a[href], video")].filter(
          (el) => !el.disabled
        );
        if (!els.length) return;
        const first = els[0];
        const last = els[els.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        } else if (!root.contains(document.activeElement)) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose, onStep]);

  // swipe follows the finger, then commits or springs back
  const onTouchStart = (e) => {
    touchX.current = e.touches[0].clientX;
    const inner = innerRef.current;
    if (inner) inner.style.transition = "none";
  };
  const onTouchMove = (e) => {
    if (touchX.current === null) return;
    const inner = innerRef.current;
    if (!inner) return;
    const dx = e.touches[0].clientX - touchX.current;
    inner.style.transform = `translateX(${dx * 0.85}px)`;
    inner.style.opacity = String(Math.max(0.5, 1 - Math.abs(dx) / 600));
  };
  const onTouchEnd = (e) => {
    if (touchX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    touchX.current = null;
    const inner = innerRef.current;
    if (inner) {
      inner.style.transition = "transform 0.25s ease-out, opacity 0.25s ease-out";
      inner.style.transform = "";
      inner.style.opacity = "";
    }
    if (Math.abs(dx) > 70) onStep(dx < 0 ? 1 : -1);
  };

  if (!item) return null;
  return (
    <div
      ref={rootRef}
      className="glb"
      role="dialog"
      aria-modal="true"
      aria-label="Photo viewer"
      onClick={onClose}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      <div ref={innerRef} className="glb__inner" onClick={(e) => e.stopPropagation()}>
        {item.kind === "video" ? (
          <video className="glb__media" src={item.full} controls autoPlay playsInline />
        ) : (
          <img className="glb__media" src={item.full} alt="Wedding photo" />
        )}
        <div className="glb__bar">
          <span className="glb__caption" />{/* no labels — classifications stay private */}
          <button
            className="glb__save"
            onClick={(e) => {
              e.stopPropagation();
              saveMedia(item);
            }}
          >
            <Download size={15} strokeWidth={1.5} /> Save
          </button>
          <span className="glb__count">
            {index + 1} / {items.length}
          </span>
        </div>
      </div>
      <button className="glb__btn glb__btn--prev" aria-label="Previous" onClick={(e) => { e.stopPropagation(); onStep(-1); }}>
        <ArrowLeft size={20} strokeWidth={1.5} />
      </button>
      <button className="glb__btn glb__btn--next" aria-label="Next" onClick={(e) => { e.stopPropagation(); onStep(1); }}>
        <ArrowRight size={20} strokeWidth={1.5} />
      </button>
      <button className="glb__btn glb__btn--close" aria-label="Close" onClick={onClose}>
        <X size={20} strokeWidth={1.5} />
      </button>
    </div>
  );
}

function UploadPanel({ onDone, onClose }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");
  const fileRef = useRef(null);

  const submit = async (e) => {
    e.preventDefault();
    const files = [...(fileRef.current?.files || [])];
    if (!files.length) {
      setStatus("Pick at least one photo or video.");
      return;
    }
    setBusy(true);
    let done = 0;
    try {
      for (const file of files) {
        setStatus(`Uploading ${done + 1} of ${files.length}…`);
        const r = await fetch("/api/upload", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ filename: file.name, contentType: file.type, uploader: name }),
        });
        const j = await r.json();
        if (!r.ok) throw new Error(j.error || "Upload refused");
        const put = await fetch(j.url, {
          method: "PUT",
          headers: { "Content-Type": file.type },
          body: file,
        });
        if (!put.ok) throw new Error("Storage upload failed");
        done += 1;
      }
      setStatus(`Added ${done} ${done === 1 ? "photo" : "photos"} — thank you!`);
      onDone();
      setTimeout(onClose, 1600);
    } catch (err) {
      setStatus(`${err.message} (${done} of ${files.length} made it — try the rest again)`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="gup" role="dialog" aria-modal="true" aria-label="Add your photos" onClick={onClose}>
      <form className="gup__card" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <span className="eyebrow">Add yours</span>
        <h2 className="font-display gup__title">The photos on your phone</h2>
        <p className="gup__lede">Add them to the album. We want every angle of that night.</p>
        <label className="gup__label" htmlFor="gup-name">Your name</label>
        <input
          id="gup-name"
          className="gup__input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="So we know who to thank"
          maxLength={40}
        />
        <label className="gup__label" htmlFor="gup-files">Photos or videos</label>
        <input id="gup-files" className="gup__file" type="file" ref={fileRef} accept="image/*,video/*" multiple />
        <p className="gup__hint">
          Add as many as you like. Videos work too — up to about 45 seconds
          (50MB) each.
        </p>
        <div className="gup__actions">
          <button type="submit" className="gup__submit" disabled={busy}>
            {busy ? "Uploading…" : "Add to the album"}
          </button>
          <button type="button" className="gup__cancel" onClick={onClose} disabled={busy}>
            Cancel
          </button>
        </div>
        <p className="gup__status" role="status">{status}</p>
      </form>
    </div>
  );
}

const GROUPS = [
  { key: "photobooth", title: "Your strips" },
  { key: "originals", title: "The originals" },
  { key: "guest", title: "From the guest album" },
];

export default function GalleryPage() {
  const [params] = useSearchParams();
  const albumParam = params.get("album");
  // find = privacy-first name search (default); social + guest stay browsable
  const mode = albumParam === "social" || albumParam === "guest" ? albumParam : "find";
  const [q, setQ] = useState(() => params.get("q") || "");
  const [shareMsg, setShareMsg] = useState("");

  // keep the search in the URL so "my photos" is a shareable link
  useEffect(() => {
    if (mode !== "find") return;
    const url = new URL(window.location.href);
    if (q) url.searchParams.set("q", q);
    else url.searchParams.delete("q");
    window.history.replaceState(null, "", url);
  }, [q, mode]);

  // Download a whole result group as one zip, built in the browser
  const [zipBusy, setZipBusy] = useState("");
  const downloadAll = async (group) => {
    setZipBusy(group.key);
    try {
      const { default: JSZip } = await import("jszip");
      const zip = new JSZip();
      let n = 0;
      for (const it of group.items) {
        const blob = await (await fetch(it.full)).blob();
        const ext = it.kind === "video" ? "mp4" : "jpg";
        zip.file(`gilmore-wedding-${String(++n).padStart(2, "0")}.${ext}`, blob);
      }
      const out = await zip.generateAsync({ type: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(out);
      a.download = `gilmore-wedding-${norm(q) || "photos"}-${group.key}.zip`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 30000);
    } catch {
      setShareMsg("The zip hit a snag — try saving photos one at a time.");
    }
    setZipBusy("");
  };

  const shareResults = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ title: `${SITE.couple} — wedding photos`, url });
        return;
      }
    } catch (err) {
      if (err && err.name === "AbortError") return;
    }
    try {
      await navigator.clipboard.writeText(url);
      setShareMsg("Link copied — send it to whoever's in these.");
      setTimeout(() => setShareMsg(""), 3000);
    } catch {
      setShareMsg(url);
    }
  };
  const [items, setItems] = useState(null);
  const [aliases, setAliases] = useState({});
  const [error, setError] = useState("");
  const [lightbox, setLightbox] = useState(-1);
  const [showUpload, setShowUpload] = useState(false);
  const [sugIdx, setSugIdx] = useState(-1);
  const lastRefresh = useRef(0);

  // Signed URLs live for an hour — if media errors (expired token on a
  // long-open tab), quietly re-mint the list once every few minutes.
  const refresh = useCallback(() => {
    if (Date.now() - lastRefresh.current < 240000) return;
    lastRefresh.current = Date.now();
    const album = mode === "social" ? "social" : "all";
    fetch(`/api/gallery?album=${album}&_=${Date.now()}`)
      .then((r) => r.json())
      .then((j) => {
        if (!j.error) {
          setItems(j.items);
          setAliases(j.aliases || {});
        }
      })
      .catch(() => {});
  }, [mode]);

  const load = useCallback(
    (bustCache = false) => {
      setItems(null);
      setError("");
      const album = mode === "social" ? "social" : "all";
      const bust = bustCache ? `&_=${Date.now()}` : "";
      fetch(`/api/gallery?album=${album}${bust}`)
        .then((r) => r.json())
        .then((j) => {
          if (j.error) throw new Error(j.error);
          setItems(j.items);
          setAliases(j.aliases || {});
        })
        .catch((e) => setError(e.message));
    },
    [mode]
  );

  useEffect(() => load(), [load]);
  useEffect(() => window.scrollTo(0, 0), [mode]);

  // ---- find mode: nothing shows until a name is searched ----
  const nq = norm(q);
  const wanted = useMemo(() => [nq, aliases[nq]].filter(Boolean), [nq, aliases]);
  const searched = mode === "find" && nq.length >= 2;

  const results = useMemo(() => {
    if (!searched || !items) return [];
    return GROUPS.map((g) => ({
      ...g,
      items: items.filter(
        (i) => i.album === g.key && wanted.some((w) => norm(i.caption).includes(w))
      ),
    })).filter((g) => g.items.length > 0);
  }, [searched, items, wanted]);

  // progressive name suggestions — only names matching what's typed
  const suggestions = useMemo(() => {
    if (mode !== "find" || !items || nq.length < 2 || !q) return [];
    const set = new Set();
    items.forEach((i) => (i.caption || "").split(" · ").forEach((n) => n && set.add(n)));
    return [...set]
      .filter((n) => norm(n).includes(nq))
      .sort((a, b) => (norm(b).startsWith(nq) ? 1 : 0) - (norm(a).startsWith(nq) ? 1 : 0) || a.localeCompare(b))
      .slice(0, 6);
  }, [mode, items, nq, q]);
  const exactMatch = suggestions.length === 1 && norm(suggestions[0]) === nq;
  useEffect(() => setSugIdx(-1), [q]);

  const onSearchKey = (e) => {
    if (!suggestions.length || exactMatch) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSugIdx((i) => (i + 1) % suggestions.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSugIdx((i) => (i - 1 + suggestions.length) % suggestions.length);
    } else if (e.key === "Enter" && sugIdx >= 0) {
      e.preventDefault();
      setQ(suggestions[sugIdx]);
    } else if (e.key === "Escape") {
      setSugIdx(-1);
    }
  };

  const shown = useMemo(() => {
    if (mode === "find") return results.flatMap((g) => g.items);
    // The Guest Album is everything shared or downloaded — guest uploads,
    // the old app's photos, and the full One Take library. Booth photos
    // stay behind the name search.
    if (mode === "guest")
      return (items || []).filter((i) => i.album === "guest" || i.album === "social");
    return items || [];
  }, [mode, results, items]);

  useReveal([shown]);

  const step = useCallback(
    (d) => setLightbox((i) => (i + d + shown.length) % shown.length),
    [shown.length]
  );

  const HEAD = {
    find: {
      title: "Find your photos",
      lede: "Type your last name. Everything you're in comes straight to you.",
    },
    social: {
      title: "The Social Reel",
      lede: "The weekend in motion, by One Take Bridal. Save anything you love.",
    },
    guest: {
      title: "The Guest Album",
      lede: "What everyone shared, and room for yours.",
    },
  }[mode];

  // Highlights: One Take's edited finals + the reel, as a carousel
  const highlights = useMemo(
    () => (items || []).filter((i) => /^social-00\d_/.test(i.id) || i.id.includes("000_the-reel")),
    [items]
  );
  const [hlIndex, setHlIndex] = useState(-1);
  const hlRow = useRef(null);
  const hlOrigin = useRef(null);
  const hlStep = useCallback(
    (d) => setHlIndex((i) => (i + d + highlights.length) % highlights.length),
    [highlights.length]
  );

  // gentle auto-advance; pauses for touch/hover, off under reduced motion
  useEffect(() => {
    const row = hlRow.current;
    if (!row || highlights.length < 2) return undefined;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    let paused = false;
    const setPause = (v) => () => { paused = v; };
    row.addEventListener("pointerenter", setPause(true));
    row.addEventListener("pointerleave", setPause(false));
    row.addEventListener("touchstart", setPause(true), { passive: true });
    const t = setInterval(() => {
      if (paused || document.hidden) return;
      const w = row.firstChild ? row.firstChild.offsetWidth + 14 : 260;
      const atEnd = row.scrollLeft + row.clientWidth >= row.scrollWidth - 10;
      row.scrollTo({ left: atEnd ? 0 : row.scrollLeft + w, behavior: "smooth" });
    }, 4000);
    return () => clearInterval(t);
  }, [highlights.length]);

  const originRect = useRef(null);
  const renderCell = (item) => {
    const idx = shown.indexOf(item);
    return (
      <div key={item.id} className="ggrid__cell">
        <button
          type="button"
          className="ggrid__open"
          aria-label="View photo"
          onClick={(e) => {
            originRect.current = e.currentTarget.closest(".ggrid__cell").getBoundingClientRect();
            setLightbox(idx);
          }}
        >
          {item.kind === "video" && item.thumb === item.full ? (
            <video className="ggrid__media" src={`${item.thumb}#t=0.1`} preload="metadata" muted playsInline onError={refresh} />
          ) : (
            <img className="ggrid__media" src={item.thumb} alt="" loading="lazy" onError={refresh} />
          )}
          {item.kind === "video" && <span className="ggrid__play" aria-hidden="true">▶</span>}
        </button>
        <CellSave item={item} />
      </div>
    );
  };

  return (
    <div className="gpage" id="main">
      <header className="gpage__nav">
        <Link to="/" className="gpage__back">
          <ArrowLeft size={16} strokeWidth={1.5} /> {SITE.couple}
        </Link>
        <button className="gpage__add" onClick={() => setShowUpload(true)}>
          <Plus size={16} strokeWidth={1.5} /> Add your photos
        </button>
      </header>

      <div className="gpage__head">
        <span className="eyebrow">The Album</span>
        <h1 className="font-display gpage__title">{HEAD.title}</h1>
        <p className="gpage__lede">{HEAD.lede}</p>
        {mode !== "find" && (
          <p className="gpage__switch">
            <Link to="/gallery">Looking for your booth photos? Find them by name →</Link>
          </p>
        )}
      </div>

      {mode === "find" && (
        <div className="gsearch">
          <div className="gsearch__hero gsearch__hero--tall" style={{ backgroundImage: "url(/media/booth-bench.jpg)" }}>
            <div className="gsearch__stack">
              <div className="gsearch__box gsearch__box--glass">
                <Search size={16} strokeWidth={1.5} aria-hidden="true" />
                <input
                  type="search"
                  className="gsearch__input"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  onKeyDown={onSearchKey}
                  placeholder="Your last name"
                  aria-label="Search by last name"
                  autoComplete="off"
                  role="combobox"
                  aria-expanded={suggestions.length > 0 && !exactMatch}
                  aria-controls="gsearch-listbox"
                  aria-activedescendant={sugIdx >= 0 ? `gsug-${sugIdx}` : undefined}
                />
                {q && (
                  <button className="gsearch__clear" aria-label="Clear search" onClick={() => setQ("")}>
                    <X size={15} strokeWidth={1.5} />
                  </button>
                )}
              </div>
              {suggestions.length > 0 && !exactMatch && (
                <div className="gsearch__suggest" id="gsearch-listbox" role="listbox" aria-label="Matching names">
                  {suggestions.map((n, i) => (
                    <button
                      key={n}
                      id={`gsug-${i}`}
                      role="option"
                      aria-selected={i === sugIdx}
                      className={`gsearch__suggestion ${i === sugIdx ? "gsearch__suggestion--on" : ""}`}
                      onClick={() => setQ(n)}
                    >
                      {n}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          <p className="gsearch__privacy">
            Search your last name to find your strip.
          </p>
        </div>
      )}

      {mode === "find" && !searched && highlights.length > 0 && (
        <section className="ghl" aria-label="Highlights">
          <div className="ghl__head">
            <span className="eyebrow">Highlights</span>
            <h2 className="font-display ghl__title">The best of the night</h2>
          </div>
          <div className="ghl__row" ref={hlRow}>
            {highlights.map((h, i) => (
              <button
                key={h.id}
                type="button"
                className="ghl__slide"
                aria-label="Play highlight"
                onClick={(e) => {
                  hlOrigin.current = e.currentTarget.getBoundingClientRect();
                  setHlIndex(i);
                }}
              >
                <img src={h.thumb} alt="" loading="lazy" />
                <span className="ghl__play" aria-hidden="true">▶</span>
              </button>
            ))}
          </div>
          <p className="ghl__cta">
            Have your own from the night? <button className="ghl__ctalink" onClick={() => setShowUpload(true)}>Add them to the album</button> — photos and videos both.
          </p>
        </section>
      )}

      {error && (
        <div className="gpage__state">
          <p>The album hit a snag: {error}</p>
          <button className="gpage__chip" onClick={() => load()}>Try again</button>
        </div>
      )}
      {!error && items === null && <div className="gpage__state"><p>Opening the album…</p></div>}

      {mode === "find" && !error && items !== null && searched && results.length === 0 && (
        <div className="gpage__state">
          <p>Nothing under “{q}” yet — try another spelling, or a maiden name.</p>
        </div>
      )}

      {mode === "find" && results.length > 0 && (
        <div className="gshare">
          <button className="gpage__chip" onClick={shareResults}>
            Share these photos
          </button>
          <p className="gshare__msg" role="status">{shareMsg}</p>
        </div>
      )}

      {mode === "find" &&
        results.map((g) => (
          <section key={g.key} className="gresults">
            <div className="gresults__head">
              <div>
                <h2 className="gresults__title font-display">{g.title}</h2>
                <p className="gresults__count">
                  {g.items.length} {g.items.length === 1 ? "photo" : "photos"}
                </p>
              </div>
              <button className="gpage__chip" onClick={() => downloadAll(g)} disabled={zipBusy === g.key}>
                {zipBusy === g.key ? "Zipping…" : "Download all"}
              </button>
            </div>
            <div className="ggrid">{g.items.map(renderCell)}</div>
          </section>
        ))}

      {mode !== "find" && !error && items !== null && shown.length === 0 && (
        <div className="gpage__state">
          <p>
            {mode === "guest"
              ? "Nothing here yet — be the first to add the photos on your phone."
              : "The reel lands here soon."}
          </p>
        </div>
      )}
      {mode !== "find" && <div className="ggrid">{shown.map(renderCell)}</div>}

      {lightbox >= 0 && (
        <Lightbox items={shown} index={lightbox} origin={originRect.current} onClose={() => setLightbox(-1)} onStep={step} />
      )}
      {hlIndex >= 0 && (
        <Lightbox items={highlights} index={hlIndex} origin={hlOrigin.current} onClose={() => setHlIndex(-1)} onStep={hlStep} />
      )}
      {showUpload && <UploadPanel onDone={() => load(true)} onClose={() => setShowUpload(false)} />}
    </div>
  );
}
